import { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Package, LayoutGrid, PlusCircle, Inbox, Image, Settings as SettingsIcon, LogOut, Menu, X,
} from 'lucide-react';
import Logo from '../components/Logo';

const nav = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/categories', label: 'Categories', icon: LayoutGrid },
  { to: '/admin/packages', label: 'Packages', icon: Package },
  { to: '/admin/addons', label: 'Add-ons', icon: PlusCircle },
  { to: '/admin/inquiries', label: 'Inquiries', icon: Inbox },
  { to: '/admin/content', label: 'Content', icon: Image },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon },
];

export function isAuthed() {
  return Boolean(localStorage.getItem('es_admin_token'));
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!isAuthed()) return <Navigate to="/admin" replace />;

  const logout = () => {
    localStorage.removeItem('es_admin_token');
    navigate('/admin');
  };

  return (
    <div className="flex min-h-screen bg-ink-950">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-white/10 bg-ink-900 transition-transform md:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-20 items-center justify-between px-5">
          <Logo to="/admin/dashboard" />
          <button className="md:hidden" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="space-y-1 px-3 py-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition ${
                  isActive ? 'bg-brand-gradient text-white shadow-brand-glow' : 'text-ink-300 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
          <button
            onClick={logout}
            className="mt-6 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-danger hover:bg-danger/10"
          >
            <LogOut size={18} /> Logout
          </button>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64">
        <header className="flex h-16 items-center gap-3 border-b border-white/10 px-5 md:hidden">
          <button onClick={() => setOpen(true)}><Menu size={22} /></button>
          <Logo to="/admin/dashboard" showText={false} />
          <span className="font-display font-semibold">Admin</span>
        </header>
        <motion.main
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 sm:p-8"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
